import React, { useEffect, useMemo, useRef, useState, useId } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    closestCenter,
    defaultDropAnimationSideEffects,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "../../atoms/Button";
import Select from "../../atoms/Select";
import GlassDatePicker from "../../molecules/GlassDatePicker";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { getBoardPriorityOptions } from "./BandBoardCardModal";
import BandBoardListActions from "../../molecules/Bands/BandBoardListActions";
import "./BandBoardPanel.scss";

function SortableColumn({ column, children }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: column.id,
        data: { type: "column" },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <section ref={setNodeRef} style={style} className="band-board__column" {...attributes} {...listeners}>
            {children}
        </section>
    );
}

SortableColumn.propTypes = {
    column: PropTypes.shape({ id: PropTypes.string }).isRequired,
    children: PropTypes.node.isRequired,
};

function SortableCard({ card, listId, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: card.id,
        data: { type: "card", card, listId },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
    };

    return (
        <article
            ref={setNodeRef}
            style={style}
            className={`band-board__card${isDragging ? " band-board__card--dragging" : ""}`}
            {...attributes}
            {...listeners}
        >
            {children}
        </article>
    );
}

SortableCard.propTypes = {
    card: PropTypes.shape({ id: PropTypes.string }).isRequired,
    listId: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

function BandBoardCard({ card, onSelect }) {
    const { t } = useTranslation();
    const checklistItems = useMemo(
        () => (card.checklists || []).flatMap((checklist) => checklist.items || []),
        [card.checklists]
    );
    const totalChecklistItems = checklistItems.length;
    const completedChecklistItems = checklistItems.filter((item) => item.completed).length;
    const checklistProgress = totalChecklistItems > 0
        ? Math.round((completedChecklistItems / totalChecklistItems) * 100)
        : null;

    const priorityLabel = card.priority ? card.priority.toLowerCase() : "medium";
    const priorityText = t(`bandsPage.board.priority.${priorityLabel}`, { defaultValue: card.priority || "MEDIUM" });
    const dueDate = card.dueAt ? new Date(card.dueAt) : null;

    return (
        <button type="button" className="band-board-card" onClick={onSelect}>
            <header className="band-board-card__header">
                <span className={`band-board-card__priority band-board-card__priority--${priorityLabel}`}>
                    {priorityText}
                </span>
                {(dueDate || card.workItemId) && (
                    <div className="band-board-card__meta">
                        {dueDate && (
                            <time className="band-board-card__due">
                                {dueDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            </time>
                        )}
                        {card.workItemId && (
                            <span
                                className="band-board-card__work-id"
                                title={t("bandsPage.board.card.workItem", { id: card.workItemId })}
                                aria-label={t("bandsPage.board.card.workItem", { id: card.workItemId })}
                            >
                                {card.workItemId}
                            </span>
                        )}
                    </div>
                )}
            </header>
            <h4 className="band-board-card__title" title={card.title}>
                {card.title}
            </h4>
            {card.description && (
                <p className="band-board-card__description" title={card.description}>
                    {card.description}
                </p>
            )}
            <footer className="band-board-card__footer">
                <span>
                    {card.storyPoints != null
                        ? t("bandsPage.board.card.storyPoints", { points: card.storyPoints })
                        : t("bandsPage.board.card.noEstimate")}
                </span>
                {checklistProgress != null ? (
                    <span>{t("bandsPage.board.card.checklistProgress", { percent: checklistProgress })}</span>
                ) : (
                    <span>{t("bandsPage.board.card.checklistReady")}</span>
                )}
            </footer>
        </button>
    );
}

BandBoardCard.propTypes = {
    card: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        dueAt: PropTypes.string,
        workItemId: PropTypes.string,
        priority: PropTypes.string,
        storyPoints: PropTypes.number,
        checklists: PropTypes.array,
    }).isRequired,
    onSelect: PropTypes.func.isRequired,
};

const WorkspaceBoardForm = React.forwardRef(function WorkspaceBoardForm(
    { variant, className = "", onSubmit = undefined, children },
    ref
) {
    const formClassName = [
        "workspace-board__form",
        `workspace-board__form--${variant}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const handleSubmit = async (event) => {
        event.preventDefault();
        await onSubmit?.(event);
    };

    return (
        <form ref={ref} className={formClassName} onSubmit={handleSubmit}>
            {children}
        </form>
    );
});

WorkspaceBoardForm.propTypes = {
    variant: PropTypes.oneOf(["inline", "card", "flow"]).isRequired,
    className: PropTypes.string,
    onSubmit: PropTypes.func,
    children: PropTypes.node.isRequired,
};

export default function BandBoardPanel({
    board = null,
    boardOptions = [],
    activeBoardId = "",
    onSelectBoard = () => {},
    isLoading = false,
    newFlowDraft,
    onFlowDraftChange,
    onCreateFlow,
    newListName = "",
    onNewListNameChange,
    onCreateList,
    newCardDraft,
    onCardDraftChange,
    onCreateCard,
    onDragEnd = null,
    onSelectCard,
    onRequestEditList = undefined,
    onRequestDeleteList = undefined,
}) {
    const { t } = useTranslation();
    const switcherId = useId();
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [activeForm, setActiveForm] = useState(null);
    const [submittingForm, setSubmittingForm] = useState(null);
    const [activeCard, setActiveCard] = useState(null);
    const addMenuRef = useRef(null);
    const flowFormRef = useRef(null);
    const columnFormRef = useRef(null);
    const workFormRef = useRef(null);

    useEffect(() => {
        if (!isAddMenuOpen && !activeForm) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            if (
                isAddMenuOpen
                && addMenuRef.current
                && !addMenuRef.current.contains(event.target)
            ) {
                setIsAddMenuOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsAddMenuOpen(false);
                setActiveForm(null);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [activeForm, isAddMenuOpen]);

    useEffect(() => {
        const refMap = {
            flow: flowFormRef,
            column: columnFormRef,
            work: workFormRef,
        };
        const targetForm = activeForm ? refMap[activeForm]?.current : null;
        if (!targetForm) {
            return;
        }
        const focusTarget = targetForm.querySelector("input, textarea, select");
        if (focusTarget) {
            focusTarget.focus();
        }
    }, [activeForm]);

    useEffect(() => {
        setActiveForm(null);
        setIsAddMenuOpen(false);
    }, [board?.id]);

    const priorityOptions = useMemo(() => getBoardPriorityOptions(t), [t]);

    const handleMenuToggle = () => {
        setIsAddMenuOpen((current) => !current);
    };

    const handleSelectForm = (form) => {
        setActiveForm(form);
        setIsAddMenuOpen(false);
    };

    const handleSubmitForm = async (type, action) => {
        if (!action) {
            return;
        }
        setSubmittingForm(type);
        try {
            await action();
            setActiveForm(null);
        } catch (_error) {
            // Parent handlers manage error reporting; keep form open.
        } finally {
            setSubmittingForm(null);
        }
    };

    if (isLoading) {
        return (
            <div className="workspace-placeholder">
                <LoadingSpinner size={40} />
            </div>
        );
    }

    if (!board) {
        return (
            <div className="workspace-placeholder">
                <p>{t("bandsPage.board.empty")}</p>
            </div>
        );
    }

    const handleDragStart = (event) => {
        const data = event.active?.data?.current;
        if (data?.type === "card" && data.card) {
            setActiveCard(data.card);
        }
    };

    const handleDragEnd = (event) => {
        setActiveCard(null);
        if (!onDragEnd) {
            return;
        }
        onDragEnd(event);
    };

    const handleDragCancel = () => {
        setActiveCard(null);
    };

    const dropAnimation = {
        duration: 220,
        easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: { opacity: "0" },
            },
        }),
    };

    const isFlowFormActive = activeForm === "flow";
    const isColumnFormActive = activeForm === "column";
    const isWorkFormActive = activeForm === "work";
    const isFlowSubmitting = submittingForm === "flow";
    const isColumnSubmitting = submittingForm === "column";
    const isWorkSubmitting = submittingForm === "work";

    const totalCards = board.lists?.reduce((sum, list) => sum + (list.cards?.length || 0), 0) || 0;
    const availableBoards = (boardOptions && boardOptions.length > 0)
        ? boardOptions
        : [{ id: board.id, name: board.name }];
    const canSwitchBoards = Boolean(boardOptions?.length > 1 && typeof onSelectBoard === "function");
    const currentBoardValue = activeBoardId || board.id || availableBoards[0]?.id || "";
    const boardSelectOptions = availableBoards.map((option) => ({
        value: option.id,
        label: option.name,
    }));
    const quickFlowLabel = t("bandsPage.board.quickFlow.help");

    return (
        <div className="workspace-surface">
        <div className="workspace-board">
            <header className="workspace-board__topbar">
                <div className="workspace-board__topbar-main">
                    <div className="workspace-board__switcher-group">
                        <label className="workspace-board__switcher-label" htmlFor={switcherId}>
                            {t("bandsPage.board.switcherLabel")}
                        </label>
                        <div className={`workspace-board__switcher-shell${canSwitchBoards ? "" : " workspace-board__switcher-shell--disabled"}`}>
                            <Select
                                id={switcherId}
                                className="workspace-board__switcher"
                                value={currentBoardValue}
                                onChange={(event) => {
                                    if (canSwitchBoards) {
                                        onSelectBoard(event.target.value);
                                    }
                                }}
                                options={boardSelectOptions}
                                disabled={!canSwitchBoards}
                            />
                            <span className="workspace-board__switcher-arrow" aria-hidden="true" />
                        </div>
                    </div>
                    <div className="workspace-board__meta">
                        <span className="workspace-board__meta-chip">
                            {t("bandsPage.board.stats.columns", { count: board.lists?.length || 0 })}
                        </span>
                        <span className="workspace-board__meta-chip">
                            {t("bandsPage.board.stats.cards", { count: totalCards })}
                        </span>
                    </div>
                </div>
                <div className="workspace-board__controls">
                    <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        className="workspace-board__flow-quick-button"
                        onClick={() => handleSelectForm("flow")}
                        aria-label={quickFlowLabel}
                        title={quickFlowLabel}
                    >
                        <span className="workspace-board__flow-quick-icon" aria-hidden="true">+</span>
                        <span className="workspace-board__flow-quick-label">{quickFlowLabel}</span>
                    </Button>
                    <div className="workspace-board__add" ref={addMenuRef}>
                        <Button
                            type="button"
                            variant="gradient"
                            size="small"
                            text={t("bandsPage.board.addMenu.trigger")}
                            className="workspace-board__add-trigger"
                            onClick={handleMenuToggle}
                            aria-haspopup="menu"
                            aria-expanded={isAddMenuOpen}
                        />
                        {isAddMenuOpen && (
                            <div className="workspace-board__add-menu" role="menu">
                                <button type="button" role="menuitem" onClick={() => handleSelectForm("column")}>
                                    {t("bandsPage.board.addMenu.column")}
                                </button>
                                <button type="button" role="menuitem" onClick={() => handleSelectForm("work")}>
                                    {t("bandsPage.board.addMenu.work")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {board.description && (
                    <p className="workspace-board__description">{board.description}</p>
                )}
            </header>

            <div className="workspace-board__toolbar">
                {isFlowFormActive && (
                    <WorkspaceBoardForm
                        ref={flowFormRef}
                        variant="flow"
                        onSubmit={() => handleSubmitForm("flow", onCreateFlow)}
                    >
                        <label>
                            <span>{t("bandsPage.board.flowForm.nameLabel")}</span>
                            <input
                                value={newFlowDraft?.name || ""}
                                onChange={(event) => onFlowDraftChange("name")(event)}
                                placeholder={t("bandsPage.board.flowForm.namePlaceholder")}
                                maxLength={140}
                                disabled={isFlowSubmitting}
                            />
                        </label>
                        <label>
                            <span>{t("bandsPage.board.flowForm.descriptionLabel")}</span>
                            <textarea
                                value={newFlowDraft?.description || ""}
                                onChange={(event) => onFlowDraftChange("description")(event)}
                                placeholder={t("bandsPage.board.flowForm.descriptionPlaceholder")}
                                maxLength={280}
                                disabled={isFlowSubmitting}
                            />
                        </label>
                        <div className="workspace-board__form-actions">
                            <Button
                                type="button"
                                variant="secondary"
                                text={t("bandsPage.board.flowForm.cancel")}
                                onClick={() => setActiveForm(null)}
                                disabled={isFlowSubmitting}
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                text={isFlowSubmitting ? t("bandsPage.board.flowForm.creating") : t("bandsPage.board.flowForm.create")}
                                disabled={isFlowSubmitting || !(newFlowDraft?.name || "").trim()}
                            />
                        </div>
                    </WorkspaceBoardForm>
                )}

                {isColumnFormActive && (
                    <WorkspaceBoardForm
                        ref={columnFormRef}
                        variant="inline"
                        onSubmit={() => handleSubmitForm("column", onCreateList)}
                    >
                        <label>
                            <span>{t("bandsPage.board.columnForm.label")}</span>
                            <input
                                value={newListName}
                                onChange={(event) => onNewListNameChange(event.target.value)}
                                placeholder={t("bandsPage.board.columnForm.placeholder")}
                                disabled={isColumnSubmitting}
                            />
                        </label>
                        <div className="workspace-board__form-actions">
                            <Button
                                type="button"
                                variant="secondary"
                                text={t("bandsPage.board.columnForm.cancel")}
                                onClick={() => setActiveForm(null)}
                                disabled={isColumnSubmitting}
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                text={isColumnSubmitting ? t("bandsPage.board.columnForm.adding") : t("bandsPage.board.columnForm.add")}
                                disabled={isColumnSubmitting || !newListName.trim()}
                            />
                        </div>
                    </WorkspaceBoardForm>
                )}

                {isWorkFormActive && (
                    <WorkspaceBoardForm
                        ref={workFormRef}
                        variant="card"
                        onSubmit={() => handleSubmitForm("work", onCreateCard)}
                    >
                        <label>
                            <span>{t("bandsPage.board.workForm.columnLabel")}</span>
                            <select
                                value={newCardDraft.listId || ""}
                                onChange={(event) => onCardDraftChange("listId")(event)}
                                disabled={isWorkSubmitting}
                            >
                                <option value="" disabled>
                                    {t("bandsPage.board.workForm.columnPlaceholder")}
                                </option>
                                {board.lists.map((list) => (
                                    <option key={list.id} value={list.id}>
                                        {list.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>{t("bandsPage.board.workForm.titleLabel")}</span>
                            <input
                                value={newCardDraft.title}
                                onChange={onCardDraftChange("title")}
                                placeholder={t("bandsPage.board.workForm.titlePlaceholder")}
                                maxLength={240}
                                disabled={isWorkSubmitting}
                            />
                        </label>
                        <label>
                            <span>{t("bandsPage.board.workForm.descriptionLabel")}</span>
                            <input
                                value={newCardDraft.description}
                                onChange={onCardDraftChange("description")}
                                placeholder={t("bandsPage.board.workForm.descriptionPlaceholder")}
                                disabled={isWorkSubmitting}
                            />
                        </label>
                        <label>
                            <span>{t("bandsPage.board.workForm.dueDateLabel")}</span>
                            <GlassDatePicker
                                label={t("bandsPage.board.workForm.dueDateLabel")}
                                value={newCardDraft.dueAt || ""}
                                onChange={onCardDraftChange("dueAt")}
                                withTime
                                disabled={isWorkSubmitting}
                            />
                        </label>
                        <label>
                            <span>{t("bandsPage.board.workForm.priorityLabel")}</span>
                            <select
                                value={newCardDraft.priority || "MEDIUM"}
                                onChange={onCardDraftChange("priority")}
                                disabled={isWorkSubmitting}
                            >
                                {priorityOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>{t("bandsPage.board.workForm.storyPointsLabel")}</span>
                            <input
                                type="number"
                                min="0"
                                value={newCardDraft.storyPoints || ""}
                                onChange={onCardDraftChange("storyPoints")}
                                placeholder={t("bandsPage.board.workForm.storyPointsPlaceholder")}
                                disabled={isWorkSubmitting}
                            />
                        </label>
                        <div className="workspace-board__form-actions workspace-board__form-actions--card">
                            <Button
                                type="button"
                                variant="secondary"
                                text={t("bandsPage.board.workForm.cancel")}
                                onClick={() => setActiveForm(null)}
                                disabled={isWorkSubmitting}
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                text={isWorkSubmitting ? t("bandsPage.board.workForm.creating") : t("bandsPage.board.workForm.create")}
                                disabled={
                                    isWorkSubmitting
                                    || !newCardDraft.listId
                                    || !newCardDraft.title.trim()
                                }
                            />
                        </div>
                    </WorkspaceBoardForm>
                )}
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <div className="workspace-board__columns">
                    <SortableContext items={board.lists.map((list) => list.id)} strategy={horizontalListSortingStrategy}>
                        {board.lists.map((list) => (
                            <SortableColumn key={list.id} column={list}>
                                <header className="band-board__column-header">
                                    <h3>{list.name}</h3>
                                    <div className="band-board__column-meta">
                                        <span>{t("bandsPage.board.stats.cards", { count: list.cards?.length || 0 })}</span>
                                        <BandBoardListActions
                                            list={list}
                                            onEdit={onRequestEditList}
                                            onDelete={onRequestDeleteList}
                                        />
                                    </div>
                                </header>
                                <SortableContext items={(list.cards || []).map((card) => card.id)} strategy={verticalListSortingStrategy}>
                                    <div className="band-board__column-cards">
                                        {list.cards?.map((card) => (
                                            <SortableCard key={card.id} card={card} listId={list.id}>
                                                <BandBoardCard card={card} onSelect={() => onSelectCard(list.id, card)} />
                                            </SortableCard>
                                        ))}
                                        {(list.cards?.length || 0) === 0 && (
                                            <div className="band-board__empty">{t("bandsPage.board.list.empty")}</div>
                                        )}
                                    </div>
                                </SortableContext>
                            </SortableColumn>
                        ))}
                    </SortableContext>
                </div>
                <DragOverlay dropAnimation={dropAnimation} zIndex={9999}>
                    {activeCard ? (
                        <div className="band-board-card-drag-overlay">
                            <BandBoardCard card={activeCard} onSelect={() => {}} />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
        </div>
    );
}

BandBoardPanel.propTypes = {
    board: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        lists: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
                cards: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        workItemId: PropTypes.string,
                        title: PropTypes.string,
                        description: PropTypes.string,
                        dueAt: PropTypes.string,
                        priority: PropTypes.string,
                        storyPoints: PropTypes.number,
                        checklists: PropTypes.array,
                    })
                ),
            })
        ),
    }),
    boardOptions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            channelId: PropTypes.string,
        })
    ),
    activeBoardId: PropTypes.string,
    onSelectBoard: PropTypes.func,
    isLoading: PropTypes.bool,
    newFlowDraft: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
    }).isRequired,
    onFlowDraftChange: PropTypes.func.isRequired,
    onCreateFlow: PropTypes.func.isRequired,
    newListName: PropTypes.string,
    onNewListNameChange: PropTypes.func.isRequired,
    onCreateList: PropTypes.func.isRequired,
    newCardDraft: PropTypes.shape({
        listId: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        dueAt: PropTypes.string,
        priority: PropTypes.string,
        storyPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    onCardDraftChange: PropTypes.func.isRequired,
    onCreateCard: PropTypes.func.isRequired,
    onDragEnd: PropTypes.func,
    onSelectCard: PropTypes.func.isRequired,
    onRequestEditList: PropTypes.func,
    onRequestDeleteList: PropTypes.func,
};
